/**
 * 메일 첨부 문서뷰어 (2026-08-23).
 *
 * 메일은 첨부를 **받기만** 할 수 있었다. pdf·docx·xlsx·pptx·hwp 뷰어가 이 제품에 이미 있는데,
 * 그 진입이 `AttachmentList` 안에서 **`roomId` 가 있을 때만**(채팅 전용) 열리기 때문이다.
 *
 * ★ 같은 문제를 게시판이 이미 풀어 뒀다(`BoardDocViewer`) — 채팅의 `chatPreviewStore` 는 roomId 로
 *   스코프돼 있어 여기서 쓸 수 없으므로, **스토어는 건드리지 않고** `DocPanel` 껍데기 +
 *   채팅 렌더러만 조합한다(렌더러 수정 0). 이 파일은 그 방식을 그대로 따른 것이다.
 * ★ 열 수 없는 형식은 애초에 버튼을 안 그린다 — 판정은 `attachmentPreviewDoc()` 이 한다.
 */

import { Suspense, lazy, useEffect, useState } from 'react';
import { DocPanel } from '../../components/DocPanel/DocPanel';
import { fetchAttachmentText, type PreviewDoc } from '../../lib/preview-doc';
import { tMail } from '../../lib/mail-i18n';
import './MailDocViewer.css';

const DocxPreview = lazy(() => import('../ChatPage/ChatArea/preview/DocxPreview'));
const XlsxPreview = lazy(() => import('../ChatPage/ChatArea/preview/XlsxPreview'));
const PptxPreview = lazy(() => import('../ChatPage/ChatArea/preview/PptxPreview'));
const PdfPreview = lazy(() => import('../ChatPage/ChatArea/preview/PdfPreview'));
const HwpPreview = lazy(() => import('../ChatPage/ChatArea/preview/HwpPreview'));

interface MailDocViewerProps {
  doc: PreviewDoc;
  onClose: () => void;
}

export function MailDocViewer({ doc, onClose }: MailDocViewerProps) {
  const isText = doc.kind === 'markdown' || doc.kind === 'html';
  return (
    <div className="mdv">
      <DocPanel title={doc.title} onClose={onClose}>
        <Suspense fallback={<p className="mdv-muted">{tMail('mail.viewer.loading')}</p>}>
          {doc.kind === 'pdf' && <PdfPreview doc={doc} />}
          {doc.kind === 'docx' && <DocxPreview doc={doc} />}
          {doc.kind === 'xlsx' && <XlsxPreview doc={doc} />}
          {doc.kind === 'pptx' && <PptxPreview doc={doc} />}
          {doc.kind === 'hwp' && <HwpPreview doc={doc} />}
          {isText && <TextDoc doc={doc} />}
        </Suspense>
      </DocPanel>
    </div>
  );
}

/**
 * markdown/html 은 전용 렌더러가 채팅 말풍선 쪽 로직에 묶여 있다. 원문을 그대로 보여준다 —
 * html 을 innerHTML 로 붙이지 않는 것이 요점이다(밖에서 온 메일의 첨부는 누구나 만든다).
 */
function TextDoc({ doc }: { doc: PreviewDoc }) {
  const fileId = doc.source.kind === 'attachment' ? doc.source.fileId : null;
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setText(null);
    fetchAttachmentText(fileId)
      .then((t) => { if (!cancelled) setText(t); })
      .catch(() => { if (!cancelled) setText(''); });
    return () => { cancelled = true; };
  }, [fileId]);

  if (text === null) return <p className="mdv-muted">{tMail('mail.viewer.loading')}</p>;
  return <pre className="mdv-text">{text}</pre>;
}
